// frontend/src/components/admin/reseller/ResellerDialog.js
import React, { useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  InputAdornment,
  CircularProgress,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import PackagePricingTable from "./PackagePricingTable";

const ResellerDialog = ({
  open,
  onClose,
  onSubmit,
  loading,
  editMode,
  formData,
  setFormData,
  availablePackages,
  emailTemplates, // Add this prop
  selectedReseller,
}) => {
  const handleCustomPriceChange = (newPricing) => {
    setFormData((prev) => ({
      ...prev,
      packagePricing: newPricing,
    }));
  };
  const { t } = useTranslation();

  const handleSubmit = () => {
    onSubmit(formData); // Now includes packagePricing from formData
  };

  useEffect(() => {
    if (editMode && selectedReseller) {
      setFormData({
        username: selectedReseller.username || "",
        email: selectedReseller.email || "",
        password: "", // Empty for editing
        markupPercentage: selectedReseller.markupPercentage || 0,
        allowedPackages: selectedReseller.allowedPackages || [],
        packagePricing: selectedReseller.packagePricing || {},
      });
    }
  }, [editMode, selectedReseller, setFormData]); // Add setFormData to dependencies

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>
        {editMode ? t("reseller.edit.edit") : t("reseller.addReseller")}
      </DialogTitle>
      <DialogContent>
        {/* Basic Information Section */}
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          {t("reseller.basicInfo")}
        </Typography>
        <TextField
          autoFocus
          margin="dense"
          label={t("userForm.fields.username")}
          fullWidth
          value={formData.username}
          onChange={(e) =>
            setFormData({ ...formData, username: e.target.value })
          }
          disabled={loading}
        />
        <TextField
          margin="dense"
          label={t("userForm.fields.email")}
          type="email"
          fullWidth
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          disabled={loading}
        />
        <TextField
          margin="dense"
          label={t("userForm.fields.password")}
          type="password"
          fullWidth
          value={formData.password}
          onChange={(e) =>
            setFormData({ ...formData, password: e.target.value })
          }
          disabled={loading}
          helperText={editMode ? t("userForm.passwordEditHelp") : ""}
        />
        {!editMode && (
          <TextField
            margin="dense"
            label={t("userForm.fields.initialCredits")}
            type="number"
            fullWidth
            value={formData.initialCredits}
            onChange={(e) =>
              setFormData({
                ...formData,
                initialCredits: Number(e.target.value),
              })
            }
            disabled={loading}
          />
        )}

        {/* Markup Section */}
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          {t("reseller.pricingSettings")}
        </Typography>
        <TextField
          margin="dense"
          label={t("userForm.fields.markupPercentage")}
          type="number"
          fullWidth
          value={formData.markupPercentage}
          onChange={(e) => {
            const newValue = Math.max(
              0,
              Math.min(999.99, Number(e.target.value))
            );
            setFormData({
              ...formData,
              markupPercentage: newValue,
            });
          }}
          InputProps={{
            endAdornment: <InputAdornment position="end">%</InputAdornment>,
          }}
          helperText={t("userForm.markupHelp")}
          disabled={loading}
        />
        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel>{t("reseller.form.emailTemplate")}</InputLabel>
          <Select
            value={formData.emailTemplateId || ""}
            onChange={(e) =>
              setFormData({ ...formData, emailTemplateId: e.target.value })
            }
            label={t("reseller.form.emailTemplate")}
          >
            <MenuItem value="">{t("reseller.form.defaultTemplate")}</MenuItem>
            {emailTemplates?.map((template) => (
              <MenuItem key={template.id} value={template.id}>
                {template.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {/* Package Selection and Pricing Section */}
        <FormControl fullWidth sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t("reseller.packagesAndPricing")}
          </Typography>
          <PackagePricingTable
            packages={availablePackages}
            selectedPackages={formData.allowedPackages}
            customPricing={formData.packagePricing || {}}
            onPackageSelect={(newSelection) => {
              setFormData((prev) => ({
                ...prev,
                allowedPackages: newSelection,
              }));
            }}
            onPriceChange={handleCustomPriceChange}
            defaultMarkup={formData.markupPercentage}
            loading={loading}
          />
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t("common.actions.cancel")}
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? (
            <CircularProgress size={24} />
          ) : editMode ? (
            t("userForm.actions.update")
          ) : (
            t("userForm.actions.create")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResellerDialog;
